/** The operator's pre-check-in settings as the Pre-check-in tab's draft holds them (backend
 *  plan 2026-10-08, Section 2 "P4a" and "P2 — console API"): ONE record,
 *  `tour_operator:<id>`, whose paths are the tour editor's (`pre_check_settings.<k>` — the
 *  shared model's transforms work on them unchanged) plus `definitions`.
 *
 *  - Settings: the GET's 13 `settings` keys, each its own path.
 *  - The two field lists (`…lead_booker_fields`, `…party_member_fields`), each one value,
 *    questions included (the shared model keeps them in step with the questions' switch and
 *    codes).
 *  - Waivers: `[{waiver_id}]` in the stored order (guests see them by `sequence`).
 *  - `definitions`: the operator's STORED custom fields `[{id, name, type}]` — relabelled,
 *    retyped or removed here. A custom field is the operator's: a row of a list that a
 *    definition names shows, and is sent, with the definition's label and type
 *    (`overlayDefinitions`; the server does the same). A NEW custom field lives in the lists
 *    only — the server creates its definition from its row — so reverting the list that
 *    added it takes it away with nothing left behind.
 *
 *  Pure; the words of a change (the ledger's notes) are here too. */
import {
  asFields,
  fieldListNotes as sharedFieldListNotes,
  fieldListSummary as sharedFieldListSummary,
  isQuestion,
  PRECHECK,
  splitFields,
  STANDARD_FIELDS,
  TYPE_LABEL,
  type CustomFieldDefinition,
  type PreCheckField,
} from '@/features/precheck/model';
import type { PrecheckWaiverRow } from '@/features/precheck/editor/WaiverList';
import { recordKey, type RecordKey } from '@/features/records/draft/changeSet';

import type { OperatorPreCheckDefinition, OperatorPreCheckValues } from '@/api/operatorPrecheck';

export const OPERATOR_ENTITY = 'tour_operator';
export const operatorRec = (topId: number): RecordKey => recordKey(OPERATOR_ENTITY, topId);

/** The settings row's keys, in the GET's order — every one of `OperatorPreCheckValues`. */
export const SETTINGS_KEYS = [
  'enabled',
  'required',
  'allow_edit_after_checkin',
  'intro_text',
  'confirmation_message',
  'lead_tag_categories_enabled',
  'lead_selected_tag_categories',
  'apply_same_fields_to_party_members',
  'collect_party_members_info',
  'include_party_members_in_chat',
  'party_tag_categories_enabled',
  'party_selected_tag_categories',
  'waiver_enabled',
] as const satisfies ReadonlyArray<keyof OperatorPreCheckValues>;
// Every key, none missing (a spec addition fails the typecheck here).
type MissingSettingKey = Exclude<keyof OperatorPreCheckValues, (typeof SETTINGS_KEYS)[number]>;
const ALL_KEYS_LISTED: MissingSettingKey extends never ? true : false = true;
void ALL_KEYS_LISTED;

export const LEAD_FIELDS = PRECHECK('lead_booker_fields');
export const PARTY_FIELDS = PRECHECK('party_member_fields');
export const LIST_PATHS = [LEAD_FIELDS, PARTY_FIELDS] as const;
export const WAIVERS_PATH = PRECHECK('waivers');
export const DEFINITIONS_PATH = 'definitions';

/** The server's limits (P2 notes): a list holds at most 30 custom fields (the guest check-in
 *  takes 30 answers at most); a label at most 255 characters; the reason 3–500. */
export const MAX_CUSTOM_FIELDS = 30;
export const LABEL_MAX_LENGTH = 255;
export const REASON_MIN = 3;
export const REASON_MAX = 500;
/** The texts' cap (the server's "Too long (10,000 characters at most)"). */
export const TEXT_MAX_LENGTH = 10_000;

const STANDARD_IDS: ReadonlySet<string> = new Set(STANDARD_FIELDS.map((f) => f.id));

/** A row the operator defines (neither a standard field nor a question). */
export function isCustomRow(f: PreCheckField): boolean {
  return !STANDARD_IDS.has(f.id) && !isQuestion(f);
}

export function asDefinitions(v: unknown): CustomFieldDefinition[] {
  return Array.isArray(v)
    ? (v as unknown[]).flatMap((d) => {
        if (typeof d !== 'object' || d === null) return [];
        const { id, name, type } = d as Record<string, unknown>;
        return typeof id === 'string' ? [{ id, name: typeof name === 'string' ? name : id, type: typeof type === 'string' ? type : 'string' }] : [];
      })
    : [];
}

/** The rows of a list as guests get them: a row a definition names takes its label and type. */
export function overlayDefinitions(list: unknown, defs: readonly CustomFieldDefinition[]): PreCheckField[] {
  const byId = new Map(defs.map((d) => [d.id, d]));
  return asFields(list).map((f) => {
    const d = byId.get(f.id);
    return d && !isQuestion(f) ? { ...f, name: d.name, type: d.type } : f;
  });
}

/** Custom fields of the lists that no stored definition names — created on save, from their
 *  row (first seen: the lead booker's, then the party's). */
export function newCustomFields(lists: readonly unknown[], storedIds: ReadonlySet<string>): CustomFieldDefinition[] {
  const out = new Map<string, CustomFieldDefinition>();
  for (const list of lists) {
    for (const f of asFields(list)) {
      if (isCustomRow(f) && !storedIds.has(f.id) && !out.has(f.id)) out.set(f.id, { id: f.id, name: f.name, type: f.type });
    }
  }
  return [...out.values()];
}

/** Every path a relabel (or retype) of field `id` moves: a stored definition's entry, else
 *  every row with that id in both lists — one field, one label, wherever it is asked. */
export function fieldEdit(
  valueOf: (path: string) => unknown,
  id: string,
  edit: { name?: string; type?: string },
  storedIds: ReadonlySet<string>,
): Record<string, unknown> {
  if (storedIds.has(id)) {
    const defs = asDefinitions(valueOf(DEFINITIONS_PATH));
    return { [DEFINITIONS_PATH]: defs.map((d) => (d.id === id ? { ...d, ...edit } : d)) };
  }
  const out: Record<string, unknown> = {};
  for (const path of LIST_PATHS) {
    const rows = asFields(valueOf(path));
    if (rows.some((f) => f.id === id)) out[path] = rows.map((f) => (f.id === id ? { ...f, ...edit } : f));
  }
  return out;
}

/** The ids the lists ask (fields only). */
export function askedIds(lists: readonly unknown[]): Set<string> {
  return new Set(lists.flatMap((l) => splitFields(l).regular.map((f) => f.id)));
}

/** Why a stored definition cannot be removed (the server's rule, mirrored), or null: the
 *  operator's form (the draft's lists) asks it, a tour asks it, an answer is stored. */
export function removeBlockedReason(
  meta: Pick<OperatorPreCheckDefinition, 'used_by' | 'answers'> | undefined,
  askedByForm: boolean,
): string | null {
  const reasons: string[] = [];
  if (askedByForm) reasons.push('this operator’s form asks it');
  const tours = meta?.used_by.tours ?? 0;
  if (tours > 0) reasons.push(`${tours} ${tours === 1 ? 'tour asks' : 'tours ask'} it`);
  const answers = meta?.answers ?? 0;
  if (answers > 0) reasons.push(`${answers} ${answers === 1 ? 'answer is' : 'answers are'} stored`);
  if (reasons.length === 0) return null;
  const joined = reasons.length === 1 ? reasons[0]! : `${reasons.slice(0, -1).join(', ')} and ${reasons[reasons.length - 1]}`;
  return `Can’t remove this field: ${joined}.`;
}

/** Why a stored definition's type is locked (answers exist), or null. */
export function typeLockReason(meta: Pick<OperatorPreCheckDefinition, 'answers'> | undefined): string | null {
  const n = meta?.answers ?? 0;
  return n > 0 ? `Locked: ${n} ${n === 1 ? 'answer is' : 'answers are'} stored for this field` : null;
}

export function asWaiverRows(v: unknown): PrecheckWaiverRow[] {
  return Array.isArray(v)
    ? (v as unknown[]).filter(
        (w): w is PrecheckWaiverRow => typeof w === 'object' && w !== null && typeof (w as { waiver_id?: unknown }).waiver_id === 'number',
      )
    : [];
}

export const typeLabel = (t: string): string => TYPE_LABEL[t] ?? t;

// ---------------------------------------------------------------------------
// Words (the ledger's notes under a change)
// ---------------------------------------------------------------------------

/** What changed in a field list, in words — its custom rows named by `defs`. */
export function fieldListNotes(from: unknown, to: unknown, defs: readonly CustomFieldDefinition[]): string[] {
  return sharedFieldListNotes(overlayDefinitions(from, defs), overlayDefinitions(to, defs));
}

/** The same in one line when the counts did not move (see the shared `fieldListSummary`). */
export function fieldListSummary(from: unknown, to: unknown, defs: readonly CustomFieldDefinition[]): string | null {
  return sharedFieldListSummary(overlayDefinitions(from, defs), overlayDefinitions(to, defs));
}

/** Waivers added and removed, by name. */
export function waiverNotes(from: unknown, to: unknown, nameOf: (id: number) => string): string[] {
  const was = asWaiverRows(from).map((w) => w.waiver_id);
  const now = asWaiverRows(to).map((w) => w.waiver_id);
  return [
    ...now.filter((id) => !was.includes(id)).map((id) => `Added “${nameOf(id)}”`),
    ...was.filter((id) => !now.includes(id)).map((id) => `Removed “${nameOf(id)}”`),
  ];
}

/** Custom fields relabelled, retyped or removed. */
export function definitionNotes(from: unknown, to: unknown): string[] {
  const was = new Map(asDefinitions(from).map((d) => [d.id, d]));
  const now = asDefinitions(to);
  const out: string[] = [];
  for (const d of now) {
    const old = was.get(d.id);
    if (!old) continue;
    if (old.name !== d.name) out.push(`“${old.name}” renamed “${d.name}”`);
    if (old.type !== d.type) out.push(`“${d.name}”: ${typeLabel(old.type)} → ${typeLabel(d.type)}`);
  }
  const ids = new Set(now.map((d) => d.id));
  for (const d of was.values()) if (!ids.has(d.id)) out.push(`Removed “${d.name}”`);
  return out;
}
